"use client";

import { COLOR_THEMES, installColorConfig } from "@hopper-b2b/types";
import { getDarkModePreferred } from "@hopper-b2b/utilities";
import {
  Box,
  BoxProps,
  CssBaseline,
  ScopedCssBaseline,
  StyledEngineProvider,
  Theme,
  ThemeProvider,
} from "@mui/material";
import {
  ForwardedRef,
  PropsWithChildren,
  forwardRef,
  useEffect,
  useMemo,
} from "react";
import { hopperDarkVariables, muiDarkTheme } from "./darkTheme";
import { hopperVariables, muiTheme } from "./defaultTheme";
import { hdsTheme, hdsThemeOnPhoto, hdsThemeOnPrimary } from "./hdsTheme";
import { ColorMode } from "./types";

// Temporary workaround for the issue with react types: https://github.com/mui/material-ui/issues/42020
declare const UNDEFINED_VOID_ONLY: unique symbol;
declare module "react" {
  export type VoidOrUndefinedOnly = void | { [UNDEFINED_VOID_ONLY]: never };
}

export type HopperThemingProviderProps = {
  enableDarkMode?: boolean;
  enableHDSTheme?: boolean;
  colorMode?: ColorMode;
};

export const HopperThemingProvider = forwardRef(
  (
    {
      children,
      enableDarkMode,
      enableHDSTheme,
      colorMode = "default",
      ...rest
    }: PropsWithChildren<HopperThemingProviderProps> & BoxProps,
    ref: ForwardedRef<HTMLDivElement>
  ) => {
    let defaultTheme: Theme;
    switch (colorMode) {
      case "onPrimary":
        defaultTheme = hdsThemeOnPrimary;
        break;
      case "onPhoto":
        defaultTheme = hdsThemeOnPhoto;
        break;
      default:
        defaultTheme = hdsTheme;
    }
    const isDarkMode = enableDarkMode || false;

    const isDarkModePreferred = useMemo(() => {
      const colorScheme = getDarkModePreferred()
        ? COLOR_THEMES.DARK
        : COLOR_THEMES.LIGHT;
      return isDarkMode && colorScheme === COLOR_THEMES.DARK;
    }, [isDarkMode]);

    const theme = useMemo(() => {
      if (isDarkModePreferred) {
        return muiDarkTheme;
      }
      return enableHDSTheme ? defaultTheme : muiTheme;
    }, [isDarkModePreferred, enableHDSTheme, defaultTheme]);

    useEffect(() => {
      installColorConfig(
        isDarkModePreferred ? hopperDarkVariables : hopperVariables
      );
    }, [isDarkModePreferred]);

    return (
      <StyledEngineProvider injectFirst>
        <ThemeProvider theme={theme}>
          {enableHDSTheme ? (
            <>
              <CssBaseline />
              <Box component={ScopedCssBaseline} {...rest} ref={ref}>
                {children}
              </Box>
            </>
          ) : (
            <>
              <CssBaseline />
              {children}
            </>
          )}
        </ThemeProvider>
      </StyledEngineProvider>
    );
  }
);
